import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { GeoLocationFilters } from "../components/GeoLocationFilters";
import { PageHeader } from "../components/PageHeader";
import { useAppState } from "../context/AppStateContext";
import { useGeoDirectory } from "../context/GeoDirectoryContext";
import type { ApplicationStatus } from "../types";
import { applicationMatchesGeoFilter } from "../utils/applicationGeoFilter";
import { toDisplayCamelCase } from "../utils/textCase";

const STATUS_FILTER_OPTIONS: { value: ApplicationStatus | "all"; label: string }[] = [
  { value: "all", label: "All statuses" },
  { value: "pending_validation", label: "Pending validation" },
  { value: "on_hold", label: "On hold" },
  { value: "ready_for_icici", label: "Ready for ICICI" },
  { value: "ready_for_mou", label: "Ready for MOU" },
  { value: "icici_recorded", label: "Awaiting subcontractor" },
  { value: "subcontractor_linked", label: "Subcontractor linked" },
];

function statusBadge(status: string) {
  if (status === "on_hold") return <span className="badge badge--hold">On hold</span>;
  if (status === "subcontractor_linked") return <span className="badge badge--complete">Subcontractor linked</span>;
  if (status === "icici_recorded") return <span className="badge badge--banking">Awaiting subcontractor</span>;
  if (status === "ready_for_mou") return <span className="badge badge--progress">Ready for MOU</span>;
  if (status === "ready_for_icici") return <span className="badge badge--progress">Ready for ICICI</span>;
  return <span className="badge badge--pending">Pending validation</span>;
}

export function Applications() {
  const { store: geoStore } = useGeoDirectory();
  const { applications } = useAppState();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<ApplicationStatus | "all">("all");
  const [receivedFrom, setReceivedFrom] = useState("");
  const [receivedTo, setReceivedTo] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");
  const [mandalFilter, setMandalFilter] = useState("");
  const [villageFilter, setVillageFilter] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return applications.filter((a) => {
      if (q) {
        const haystack = [
          a.mobile.applicationId,
          a.mobile.beneficiaryName,
          a.mobile.houseSanctionNumber,
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (statusFilter !== "all" && a.status !== statusFilter) return false;
      const day = a.receivedAt.slice(0, 10);
      if (receivedFrom && day < receivedFrom) return false;
      if (receivedTo && day > receivedTo) return false;
      if (!applicationMatchesGeoFilter(a, { district: districtFilter, mandal: mandalFilter, village: villageFilter }, geoStore)) {
        return false;
      }
      return true;
    });
  }, [
    applications,
    search,
    statusFilter,
    receivedFrom,
    receivedTo,
    districtFilter,
    mandalFilter,
    villageFilter,
    geoStore,
  ]);

  const hasActiveFilters =
    search.trim() !== "" ||
    statusFilter !== "all" ||
    receivedFrom !== "" ||
    receivedTo !== "" ||
    districtFilter !== "" ||
    mandalFilter !== "" ||
    villageFilter !== "";

  function clearFilters() {
    setSearch("");
    setStatusFilter("all");
    setReceivedFrom("");
    setReceivedTo("");
    setDistrictFilter("");
    setMandalFilter("");
    setVillageFilter("");
  }

  return (
    <>
      <PageHeader
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Applicants" }]}
        title="Applicants"
      />

      <div className="card card--flush card--elevated">
        <div className="table-toolbar table-toolbar--stack">
          <div className="table-toolbar__top">
            <span className="table-toolbar__count">
              {hasActiveFilters
                ? `${filtered.length} of ${applications.length} applicants`
                : `${applications.length} applicants`}
            </span>
            {hasActiveFilters ? (
              <button type="button" className="btn btn--link btn--sm table-toolbar__clear" onClick={clearFilters}>
                Clear filters
              </button>
            ) : null}
          </div>
          <div className="table-filters-grid">
            <div className="table-filters" role="search" aria-label="Filter applicants">
              <div className="field table-filters__field table-filters__field--grow">
                <label htmlFor="app-filter-search">Search</label>
                <input
                  id="app-filter-search"
                  type="search"
                  placeholder="ID, beneficiary, or sanction ref."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoComplete="off"
                />
              </div>
              <div className="field table-filters__field">
                <label htmlFor="app-filter-status">Pipeline status</label>
                <select
                  id="app-filter-status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as ApplicationStatus | "all")}
                >
                  {STATUS_FILTER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field table-filters__field">
                <label htmlFor="app-filter-from">Received from</label>
                <input
                  id="app-filter-from"
                  type="date"
                  value={receivedFrom}
                  onChange={(e) => setReceivedFrom(e.target.value)}
                />
              </div>
              <div className="field table-filters__field">
                <label htmlFor="app-filter-to">Received to</label>
                <input
                  id="app-filter-to"
                  type="date"
                  value={receivedTo}
                  onChange={(e) => setReceivedTo(e.target.value)}
                />
              </div>
            </div>
            <div className="table-filters table-filters--geo-row" aria-label="Location filters">
              <GeoLocationFilters
                idPrefix="app-geo"
                district={districtFilter}
                mandal={mandalFilter}
                village={villageFilter}
                onDistrictChange={(d) => {
                  setDistrictFilter(d);
                  setMandalFilter("");
                  setVillageFilter("");
                }}
                onMandalChange={(m) => {
                  setMandalFilter(m);
                  setVillageFilter("");
                }}
                onVillageChange={setVillageFilter}
              />
            </div>
          </div>
        </div>
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Application ID</th>
                <th>Beneficiary</th>
                <th>Sanction ref.</th>
                <th>District</th>
                <th>Mandal</th>
                <th>Village</th>
                <th>Pipeline status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="data-table__empty">
                    No applicants match the current filters.
                  </td>
                </tr>
              ) : (
                filtered.map((a) => (
                  <tr key={a.id}>
                    <td className="mono">{a.mobile.applicationId}</td>
                    <td>
                      <span className="table-name">{toDisplayCamelCase(a.mobile.beneficiaryName)}</span>
                    </td>
                    <td className="mono text-muted">{a.mobile.houseSanctionNumber}</td>
                    <td>{toDisplayCamelCase(geoStore?.findDistrict(a.district)?.registrationDistrictName ?? a.district)}</td>
                    <td>{toDisplayCamelCase(a.mandal)}</td>
                    <td>{toDisplayCamelCase(a.village)}</td>
                    <td>{statusBadge(a.status)}</td>
                    <td>
                    <Link to={`/applications/${a.id}/overview`} state={{ from: "applicants" }} className="btn btn--primary btn--sm btn--tg">
                      Open
                    </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
